import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, Boxes, GitBranch, Scale, AlertTriangle, Database, Shield,
  Swords, Cpu, Bot, FileText, ListChecks, ClipboardCheck, History, Activity, Gauge,
  TrendingUp, Mic, FlaskConical, Users, Plug, Settings, Wrench, Network, Menu, X, LogOut, Globe, ChevronDown, ShieldCheck,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard },
  { to: "/portfolio", key: "portfolio", icon: Briefcase },
  { to: "/usecases", key: "usecases", icon: Boxes },
  { to: "/lifecycle", key: "lifecycle", icon: GitBranch },
  { to: "/regulatory", key: "regulatory", icon: Scale },
  { to: "/risk", key: "risk", icon: AlertTriangle },
  { to: "/data", key: "data", icon: Database },
  { to: "/security", key: "security", icon: Shield },
  { to: "/redteam", key: "redteam", icon: Swords },
  { to: "/models", key: "models", icon: Cpu },
  { to: "/agents", key: "agents", icon: Bot },
  { to: "/rag", key: "rag", icon: Network },
  { to: "/policies", key: "policies", icon: FileText },
  { to: "/controls", key: "controls", icon: ListChecks },
  { to: "/compliance", key: "compliance", icon: ClipboardCheck },
  { to: "/audit", key: "audit", icon: History },
  { to: "/monitoring", key: "monitoring", icon: Activity },
  { to: "/kpi", key: "kpi", icon: Gauge },
  { to: "/value", key: "value", icon: TrendingUp },
  { to: "/voice", key: "voice", icon: Mic },
  { to: "/simulator", key: "simulator", icon: FlaskConical },
  { to: "/autofix", key: "autofix", icon: Wrench },
  { to: "/users", key: "users", icon: Users, admin: true },
  { to: "/portal-manager", key: "pum", icon: ShieldCheck, admin: true },
  { to: "/integrations", key: "integrations", icon: Plug },
  { to: "/admin", key: "admin", icon: Settings, admin: true },
];

export default function Shell({ children }) {
  const { t, lang, toggle } = useI18n();
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sandbox banner */}
      <div data-testid="sandbox-banner" className="fixed inset-x-0 top-0 z-50 qatar-gradient text-white">
        <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] md:text-xs font-medium">
          <span className="hidden sm:inline-flex shrink-0 rounded bg-white/20 px-2 py-0.5 font-bold tracking-wide">SANDBOX</span>
          <marquee className="flex-1" behavior="scroll" scrollamount="4">{t("banner")}</marquee>
        </div>
      </div>

      {/* Top bar */}
      <header className="fixed inset-x-0 top-[30px] z-40 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-3 backdrop-blur md:px-5">
        <button data-testid="sidebar-toggle" className="lg:hidden rounded-lg p-2 hover:bg-slate-100" onClick={() => setOpen(!open)}>{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        <div className="flex items-center gap-2.5">
          <div className="qatar-gradient grid h-9 w-9 place-items-center rounded-xl text-white font-bold font-head">Q</div>
          <div className="hidden md:block">
            <p className="text-sm font-bold font-head leading-tight text-slate-900">{t("app_name")}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("company")}</p>
          </div>
        </div>
        <div className="ms-auto flex items-center gap-2">
          <button data-testid="language-switcher" onClick={toggle} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Globe className="h-4 w-4" /> {lang === "en" ? "العربية" : "English"}
          </button>
          <div className="hidden sm:flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-1.5">
            <div className="grid h-7 w-7 place-items-center rounded-full bg-[var(--qatar-subtle)] text-xs font-bold text-[var(--qatar)]">{(user?.name || "A").charAt(0)}</div>
            <div className="leading-tight">
              <p className="text-xs font-semibold text-slate-800">{user?.name}</p>
              <p className="text-[10px] text-slate-400">{user?.role}</p>
            </div>
          </div>
          <button data-testid="logout-button" onClick={async () => { await logout(); nav("/emergent-admin-login"); }} className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"><LogOut className="h-4 w-4 flip-x" /></button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-[86px] bottom-0 z-30 w-64 overflow-y-auto border-e border-slate-200 bg-white transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"} ${lang === "ar" ? "end-0" : "start-0"}`} style={{ [lang === "ar" ? "right" : "left"]: 0 }}>
        <nav className="space-y-0.5 p-3">
          {NAV.filter((n) => !n.admin || isAdmin).map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.key}`} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "nav-item-active" : "text-slate-600 hover:bg-slate-50"}`}>
                <Icon className="h-[18px] w-[18px] shrink-0" /> <span className="truncate">{t(`nav.${n.key}`)}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className={`pt-[86px] ${lang === "ar" ? "lg:pe-64" : "lg:ps-64"}`}>
        <div className="px-4 py-6 md:px-8">{children}</div>
      </main>
    </div>
  );
}
